import api from 'api/instance';
import { FetchQueryType, FetchResponse } from 'types/common';

import { Case, Item, ItemCount, Scan, Status } from 'types/items';
import { toApiPayloadDateFormat, toMidnightISOString } from 'utils/dateUtils';

const URL = `${process.env.REACT_APP_API_URL}`;

type AdditionalDataType = {
  counts: {
    [key: string]: number; // Since the keys are dynamic (e.g., "0", "1", "2", etc.), use an index signature
  };
  /**
   * Per-site counts and the hub's active sites, for the Items by Locations
   * view (ClickUp 86bbmcuwa). Absent on snapshots written before it existed.
   */
  byLocation?: { venue: string; sku: string; count: number }[];
  sites?: { _id: string; name: string }[];
};
type FetchResponseWithAdditionalData<T> = FetchResponse<T> & {
  additionalData: AdditionalDataType;
};

export const fetchItems = async (
  params: FetchQueryType & {
    status?: number[];
    sku?: string[];
    venue?: string[];
  },
) => {
  const { data } = await api.get<FetchResponse<Item>>(`${URL}/item/list`, {
    params,
  });

  return data;
};

export const fetchItemCount = async (params: {
  washhubId?: string;
  sku?: string[];
  date?: Date;
}) => {
  const { data } = await api.get<FetchResponseWithAdditionalData<ItemCount>>(
    `${URL}/count/item-count?washHubId=${params.washhubId}`,
    {
      params: {
        skus: params.sku?.join(','),
        date: params.date ? toApiPayloadDateFormat(params.date) : undefined,
      },
    },
  );

  return data;
};

export const exportItemCountCsv = async (params: {
  washhubId?: string;
  sku?: string[];
  date?: Date;
}) => {
  const { data } = await api.get(`${URL}/count/export-item-count`, {
    params: {
      washHubId: params.washhubId,
      skus: params.sku?.join(','),
      date: params.date ? toApiPayloadDateFormat(params.date) : undefined,
    },
  });

  return data as string;
};

export const deleteItems = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/item/remove-many`, {
    data: { ids },
  });

  return data;
};

export const fetchCases = async (
  params: FetchQueryType & {
    status?: number[];
    sku?: string[];
    venue?: string[];
  },
) => {
  const { data } = await api.get<FetchResponse<Case>>(`${URL}/case/list`, {
    params,
  });

  return data;
};

export const deleteCases = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/case/remove-many`, {
    data: { ids },
  });

  return data;
};

type PayloadType = {
  washHub: string;
  washHubCode: string;
  items: Array<{
    rfid: string;
    sku: string;
    skuCode: string;
  }>;
};

export const createItem = async (payload: PayloadType) => {
  const { data } = await api.post(`${URL}/item`, payload);

  return data;
};

export const createItemWithoutEPC = async (payload: {
  washHub: string;
  sku: string;
  quantity: number;
}) => {
  const { data } = await api.post(`${URL}/item/create-without-epc`, payload);

  return data;
};

export const createCase = async (payload: any) => {
  const { data } = await api.post(`${URL}/case/create-cases`, payload);

  return data;
};

export const exportCsvCases = async ({
  washHub,
  sku,
  venue,
  status,
}: {
  washHub: string;
  sku?: string[];
  venue?: string[];
  status?: number[];
}) => {
  const { data } = await api.post(`${URL}/case/export-cases`, {
    washHub,
    sku: (sku || [])?.length > 0 ? sku : undefined,
    venue: (venue || [])?.length > 0 ? venue : undefined,
    status: (status || [])?.length > 0 ? status : undefined,
  });

  return data as string;
};

export const exportCsvItems = async ({
  washHub,
  sku,
  venue,
  status,
}: {
  washHub: string;
  sku?: string[];
  venue?: string[];
  status?: number[];
}) => {
  const { data } = await api.post(`${URL}/item/export-items`, {
    washHub,
    sku: (sku || [])?.length > 0 ? sku : undefined,
    venue: (venue || [])?.length > 0 ? venue : undefined,
    status: (status || [])?.length > 0 ? status : undefined,
  });

  return data as string;
};

export const exportCsvItemsHistory = async ({
  washHub,
  sku,
  venue,
  status,
  ...params
}: {
  washHub: string;
  sku?: string[];
  venue?: string[];
  status?: number[];
  startDate?: Date;
  endDate?: Date;
}) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.post(`${URL}/item/export-items-transactions`, {
    washHub,
    sku: (sku || [])?.length > 0 ? sku : undefined,
    venue: (venue || [])?.length > 0 ? venue : undefined,
    status: (status || [])?.length > 0 ? status : undefined,
    startDate,
    endDate,
  });

  return data as string;
};

export const exportCsvCasessHistory = async ({
  washHub,
  sku,
  venue,
  status,
  ...params
}: {
  washHub: string;
  sku?: string[];
  venue?: string[];
  status?: number[];
  startDate?: Date;
  endDate?: Date;
}) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.post(`${URL}/case/export-cases-trn`, {
    washHub,
    sku: (sku || [])?.length > 0 ? sku : undefined,
    venue: (venue || [])?.length > 0 ? venue : undefined,
    status: (status || [])?.length > 0 ? status : undefined,
    startDate,
    endDate,
  });

  return data as string;
};

type UpdateItemPayload = {
  _id: string;
  washHub: string;
  sku: string;
  rfid: string;
  size: number;
  type: string;
  qrCode?: string;
};

export const updateItem = async (payload: UpdateItemPayload) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/item/update/${_id}`, _payload);

  return data;
};

type UpdateCasePayload = {
  _id: string;
  washHub: string;
  sku: string;
  rfid: string;
  size: number;
  type: string;
};

export const updateCase = async (payload: UpdateCasePayload) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/case/update/${_id}`, _payload);

  return data;
};

export const createCaseWithoutEPC = async (payload: {
  sku: string;
  washHub: string;
  quantity: number;
}) => {
  const { data } = await api.post(`${URL}/case/create-without-epc`, payload);

  return data;
};

type ResponseTypeMap = {
  [key: number]: number;
};

export const getStatusCount = async (washHub: string) => {
  const { data } = await api.get(`${URL}/count/items-status-count`, {
    params: {
      washHub,
    },
  });

  let counts: any = {};

  data?.item?.count?.forEach((c: any) => {
    counts[c?._id] = c?.count;
  });

  return counts as ResponseTypeMap;
};

export const getCasesCount = async (washHub: string) => {
  const { data } = await api.get(`${URL}/count/cases-status-count`, {
    params: {
      washHub,
    },
  });
  let counts: any = {};

  data?.item?.count?.forEach((c: any) => {
    counts[c?._id] = c?.count;
  });

  return counts as ResponseTypeMap;
};

export const fetchScans = async (
  params: FetchQueryType & {
    status?: number[];
    sku?: string[];
    site?: string[];
    startDate?: Date;
    endDate?: Date;
  },
) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.get<FetchResponse<Scan>>(`${URL}/scan/list`, {
    params: {
      ...params,
      endDate,
      startDate,
    },
  });

  return data;
};

export const exportScansCsv = async ({
  washHub,
  sku,
  status,
  site,
  ...payload
}: {
  washHub: string;
  sku?: string[];
  site?: string[];
  status?: number[];
  startDate?: Date;
  endDate?: Date;
}) => {
  const endDate = toMidnightISOString(payload.endDate);
  const startDate = toMidnightISOString(payload.startDate);

  const { data } = await api.post<any>(`${URL}/scan/export-scans`, {
    washHub,
    sku: (sku || [])?.length > 0 ? sku : undefined,
    venue: (site || [])?.length > 0 ? site : undefined,
    status: (status || [])?.length > 0 ? status : undefined,
    startDate,
    endDate,
  });

  return data;
};

export const getItemAvailability = async (
  washHub: string,
  sku: string,
  operation: Status.TRANSFER_OUT | Status.TRANSFER_IN,
) => {
  const { data } = await api.post(`${URL}/item/get-availability-for-transfer`, {
    sku,
    washHub,
    operation,
  });

  return data?.item as number;
};

export const getCaseAvailability = async (
  washHub: string,
  sku: string,
  operation: Status.TRANSFER_OUT | Status.TRANSFER_IN,
) => {
  const { data } = await api.post(`${URL}/case/get-availability-for-transfer`, {
    sku,
    washHub,
    operation,
  });

  return data?.item as number;
};
